import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import {
  listNotifications,
  markNotificationRead,
  archiveNotification,
  isUnread,
  notificationDestination,
  type InboxItem,
} from "@/lib/brokerNotifications";
import { messageFor } from "@/lib/errors";
import { formatListDate } from "@/lib/listFormat";

const PAGE_SIZE = 50;

// The broker's in-app inbox (BRK-S157 / PMS-S353): their `notification` rows on
// the in_app channel, newest first. Merely loading the list marks the rendered
// rows SEEN server-side, so the top-bar bell badge (the UNSEEN count) clears when
// this page opens. Unread rows (readAt null) render bold; each row can be marked
// read or archived. Load/empty/error states follow the SubmissionsPage idiom.
//
// A row that HAS somewhere to go says so (BRK-S177): the `bid_result_needed`
// chaser — and since BRK-S182 the `gia_needed` reminder — carries the same deep
// link its email does, so the in-app half of one reminder is not the half that
// leaves the broker to go and find the order. The rule is
// `notificationDestination` — one pure place, so no later surface has to
// re-derive a destination.
export function NotificationsPage() {
  const { authedRequest } = useAuth();
  const [rows, setRows] = useState<InboxItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(() => {
    let cancelled = false;
    setError(null);
    setRows(null);
    (async () => {
      try {
        const page = await listNotifications(authedRequest, {
          limit: PAGE_SIZE,
        });
        if (!cancelled) setRows(page.items);
      } catch (err) {
        if (!cancelled) setError(messageFor(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest]);

  useEffect(() => load(), [load]);

  const onMarkRead = async (id: string) => {
    setBusyId(id);
    setError(null);
    try {
      await markNotificationRead(authedRequest, id);
      setRows(
        (cur) =>
          cur?.map((r) =>
            r.id === id ? { ...r, readAt: new Date().toISOString() } : r,
          ) ?? cur,
      );
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusyId(null);
    }
  };

  const onArchive = async (id: string) => {
    setBusyId(id);
    setError(null);
    try {
      await archiveNotification(authedRequest, id);
      setRows((cur) => cur?.filter((r) => r.id !== id) ?? cur);
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="flex w-full flex-col gap-6 py-8">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold text-brk-text">Notifications</h1>
        <p className="text-sm text-brk-dim">
          Updates about your submissions, bonds, and account.
        </p>
      </header>

      {error && (
        <p role="alert" className="text-sm text-brk-danger">
          {error}
        </p>
      )}
      {rows === null && !error && (
        <p role="status" className="text-sm text-brk-dim">
          Loading notifications…
        </p>
      )}
      {rows !== null && rows.length === 0 && (
        <div className="flex flex-col items-start gap-3 rounded-xl border border-dashed border-brk-border bg-brk-panel px-4 py-10">
          <p className="text-sm text-brk-text">
            You&rsquo;re all caught up — no notifications.
          </p>
        </div>
      )}

      {rows !== null && rows.length > 0 && (
        <ul className="flex flex-col gap-2">
          {rows.map((n) => (
            <li
              key={n.id}
              className="rounded-xl border border-brk-border bg-brk-panel p-4"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p
                    className={`text-sm text-brk-text ${
                      isUnread(n) ? "font-semibold" : "font-normal"
                    }`}
                  >
                    {n.subject}
                  </p>
                  {n.body && (
                    <p className="mt-1 text-sm text-brk-dim">{n.body}</p>
                  )}
                  {/* Rendered only when the row names BOTH what it is and
                      which record it is about. A notice with nowhere useful to
                      go gets no link rather than a link to a list — that would
                      answer a different question than the one the broker
                      clicked. */}
                  <NoticeLink item={n} />
                  <p className="mt-2 text-xs text-brk-dim">
                    {formatListDate(n.createdAt)}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-2 sm:flex-row sm:items-center sm:gap-3">
                  {isUnread(n) && (
                    <button
                      type="button"
                      onClick={() => onMarkRead(n.id)}
                      disabled={busyId === n.id}
                      className="text-xs font-medium text-brk-accent hover:underline disabled:opacity-50"
                    >
                      Mark read
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => onArchive(n.id)}
                    disabled={busyId === n.id}
                    className="text-xs font-medium text-brk-dim hover:underline disabled:opacity-50"
                  >
                    Archive
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** The row's deep link, when it names both what it is and which order it is
 *  about (BRK-S177, BRK-S182) — nothing otherwise. */
function NoticeLink({ item }: { item: InboxItem }) {
  const destination = notificationDestination(item);
  if (!destination) return null;
  return (
    <Link
      to={destination.to}
      className="mt-1 inline-block text-sm font-medium text-brk-accent hover:underline"
    >
      {destination.label}
    </Link>
  );
}
